/**
 * SSOT מחיר לכרטיס תוצאות חבילה דינמית (סוכן):
 * (טיסה נטו זולה ביותר) + (מלון נטו זולה ביותר מהשורה) + שכבת HR מאוחדת על הסכום — בלי חישוב ב-UI.
 */

import { agentHotelSearchLowestPackagePrice } from "../agentHotelSearchApiPrice";
import { formatIlsDisplay } from "../formatIlsDisplay";
import { formatDynamicPackageProfileSsotPackageFinalForDisplay } from "./dynamicPackageProfilePageCurrency";
import {
  pickDynamicPackageSupplierCurrency,
} from "./dynamicPackageNormalizedPricing";
import { toDealArray } from "./dynamicPackageSearchCheapestFlight";
import {
  normalizeLdsPackageChannelType,
  resolveLdsAppCommissionPercents,
} from "../../pricing/vacationPackageLdsAdapter";
import {
  legacyAppRound2,
  runUnifiedBookingPricing,
} from "../../pricing/pricingEngine.js";
import { getLdsPackagePricingChannelType } from "../../stores/customerVacationChatPricingChannel";
import { isHrDpPerfDebugEnabled } from "./dynamicPackagePerfDebug";

function isIso4217(c) {
  if (c == null || String(c).trim() === "") return false;
  return /^[A-Z]{3}$/.test(String(c).trim().toUpperCase());
}

/** One cheapest-flight scan per flight-bucket generation (shared across all hotel cards). */
let _cheapestFlightSsotCache = { key: "", bestFlight: null };

function dealListCacheToken(list) {
  const arr = toDealArray(list);
  if (arr.length === 0) return "0";
  const first = arr[0];
  const last = arr[arr.length - 1];
  const fid = first?.dealId ?? first?.ID ?? first?.dealFare?.totalFare ?? "";
  const lid = last?.dealId ?? last?.ID ?? last?.dealFare?.totalFare ?? "";
  return `${arr.length}:${fid}:${lid}`;
}

function cheapestFlightSsotCacheKey(p) {
  return [
    p.flightTripType || "RoundTrip",
    dealListCacheToken(p.roundTripFlightResults),
    dealListCacheToken(p.outboundOneWayFlightResults),
    dealListCacheToken(p.inboundOneWayFlightResults),
    dealListCacheToken(p.availableFlights),
    Array.isArray(p.categories) ? p.categories.join(",") : "",
    typeof p.calculateLDSDealFare === "function" ? "fn" : "nofn",
  ].join("|");
}

/**
 * Cheapest flight for DP SSOT — hotel-independent. Cached across card pricing calls.
 * @returns {object|null} bestFlight eval block (includes preHrIls)
 */
export function resolveCheapestFlightDealForDynamicPackageSsot(p = {}) {
  const {
    flightTripType = "RoundTrip",
    roundTripFlightResults = null,
    outboundOneWayFlightResults = null,
    inboundOneWayFlightResults = null,
    availableFlights = null,
    categories = null,
    calculateLDSDealFare,
    precomputedBestFlight = undefined,
  } = p;

  if (precomputedBestFlight !== undefined) {
    return precomputedBestFlight;
  }

  if (typeof calculateLDSDealFare !== "function") return null;

  const key = cheapestFlightSsotCacheKey(p);
  if (_cheapestFlightSsotCache.key === key) {
    return _cheapestFlightSsotCache.bestFlight;
  }

  let bestFlight = null;
  let bestPreHr = null;

  if (flightTripType === "OneWay") {
    const list = toDealArray(availableFlights);
    for (const d of list) {
      const ev = evaluateFlightDealForSsot(d, calculateLDSDealFare, categories);
      if (!ev || ev.preHrIls == null) continue;
      if (bestPreHr == null || ev.preHrIls < bestPreHr) {
        bestPreHr = ev.preHrIls;
        bestFlight = ev;
      }
    }
  } else {
    const rtList = toDealArray(roundTripFlightResults);
    for (const d of rtList) {
      const ev = evaluateFlightDealForSsot(d, calculateLDSDealFare, categories);
      if (!ev || ev.preHrIls == null) continue;
      if (bestPreHr == null || ev.preHrIls < bestPreHr) {
        bestPreHr = ev.preHrIls;
        bestFlight = ev;
      }
    }

    if (bestFlight == null) {
      const outs = toDealArray(outboundOneWayFlightResults);
      const ins = toDealArray(inboundOneWayFlightResults);
      for (const o of outs) {
        const eo = evaluateFlightDealForSsot(o, calculateLDSDealFare, categories);
        if (!eo || eo.preHrIls == null) continue;
        for (const inn of ins) {
          const ei = evaluateFlightDealForSsot(
            inn,
            calculateLDSDealFare,
            categories
          );
          if (!ei || ei.preHrIls == null) continue;
          if (eo.currency !== ei.currency) continue;
          const sum = legacyAppRound2(eo.preHrIls + ei.preHrIls);
          if (bestPreHr == null || sum < bestPreHr) {
            bestPreHr = sum;
            bestFlight = {
              rawTotal: legacyAppRound2(eo.rawTotal + ei.rawTotal),
              supplierCommission: legacyAppRound2(
                (eo.supplierCommission ?? 0) + (ei.supplierCommission ?? 0)
              ),
              netTotal: legacyAppRound2(eo.netTotal + ei.netTotal),
              currency: eo.currency,
              dealId: `${eo.dealId ?? "?"}+${ei.dealId ?? "?"}`,
              preHrIls: sum,
              _composed: { outbound: eo, inbound: ei },
            };
          }
        }
      }
    }

    if (bestFlight == null) {
      const hydrated = toDealArray(availableFlights);
      for (const d of hydrated) {
        const ev = evaluateFlightDealForSsot(d, calculateLDSDealFare, categories);
        if (!ev || ev.preHrIls == null) continue;
        if (bestPreHr == null || ev.preHrIls < bestPreHr) {
          bestPreHr = ev.preHrIls;
          bestFlight = ev;
        }
      }
    }
  }

  _cheapestFlightSsotCache = { key, bestFlight };
  return bestFlight;
}

/** Test / search-reset helper */
export function clearDynamicPackageCheapestFlightSsotCache() {
  _cheapestFlightSsotCache = { key: "", bestFlight: null };
}

/**
 * @param {object} deal
 * @param {function(object, object=): object|null} calculateLDSDealFare
 * @param {string[]|null} categories
 */
export function evaluateFlightDealForSsot(deal, calculateLDSDealFare, categories) {
  if (!deal || typeof deal !== "object") return null;
  const rawTotal = Number(deal?.dealFare?.totalFare);
  if (!Number.isFinite(rawTotal) || rawTotal <= 0) return null;
  const currency = pickDynamicPackageSupplierCurrency(deal);
  if (!isIso4217(currency)) return null;

  const dealCategory =
    deal?.category ||
    (categories && categories.length > 0 && !categories.includes("ALL")
      ? categories[0]
      : null);

  const fare = calculateLDSDealFare(
    { ...deal, dealCategory },
    { ssotBypass: true }
  );
  if (!fare?.ourTotalFare) return null;

  const netForeign = Number(fare.ourTotalFare.netPrice);
  const rate = Number(fare.ourTotalFare.currencyRate);
  if (!Number.isFinite(netForeign) || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }

  const supplierCommission = legacyAppRound2(rawTotal - netForeign);
  const preHrIls = legacyAppRound2(netForeign * rate);

  return {
    rawTotal,
    supplierCommission:
      Number.isFinite(supplierCommission) ? supplierCommission : null,
    netTotal: netForeign,
    currency: String(currency).trim().toUpperCase(),
    dealId: deal?.dealId ?? deal?.ID ?? null,
    preHrIls,
  };
}

function hotelPreHrIlsFromListRow(hotelsItem, searchPhrase, currencyRate) {
  const amt = agentHotelSearchLowestPackagePrice(hotelsItem);
  if (amt == null) return { preHrIls: null, currency: null, source: null };

  const codeRaw = searchPhrase?.currency
    ? String(searchPhrase.currency).trim().toUpperCase()
    : null;
  const listCur = isIso4217(codeRaw) ? codeRaw : null;
  if (!listCur) {
    return {
      preHrIls: null,
      currency: null,
      source: "hotelsItem.LowestPackagePrice (missing list ISO currency)",
    };
  }

  if (listCur === "ILS" || listCur === "NIS") {
    return {
      preHrIls: legacyAppRound2(amt),
      currency: "ILS",
      source: "hotelsItem.LowestPackagePrice + searchPhrase.currency (ILS)",
    };
  }

  const r = Number(currencyRate?.[listCur]);
  if (!Number.isFinite(r) || r <= 0) {
    return {
      preHrIls: null,
      currency: listCur,
      source: `hotelsItem.LowestPackagePrice (no FX rate for ${listCur})`,
    };
  }

  return {
    preHrIls: legacyAppRound2(amt * r),
    currency: "ILS",
    source: `hotelsItem.LowestPackagePrice * currencyRate[${listCur}]`,
  };
}

function applyHrOnCombinedNetIls(combinedNetIls, deps) {
  const {
    isB2CMode = false,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    getAgentCommissionPercent,
    getCreditCardFeePercent,
    hrMarginPercentOverride,
    channelTypeOverride,
  } = deps;

  const base = Number(combinedNetIls);
  if (!Number.isFinite(base) || base <= 0) return null;

  const perc = resolveLdsAppCommissionPercents({
    isHotelDeal: false,
    isB2CMode,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    getAgentCommissionPercent,
    hrMarginPercentOverride,
  });

  const ch = normalizeLdsPackageChannelType(
    channelTypeOverride ?? getLdsPackagePricingChannelType()
  );

  const unifiedInput = {
    channelType: ch,
    supplierGrossAmount: legacyAppRound2(base),
    supplierCurrency: "ILS",
    supplierCommissionType: "NET",
    hrFlatPercent: perc.selectedHrPercent,
    channelDefaultAgentPercent: 0,
    installmentsFeePercent:
      typeof getCreditCardFeePercent === "function"
        ? getCreditCardFeePercent()
        : 0,
    settlementCurrency: "ILS",
    displayCurrency: "ILS",
    hotelFinalChargeInteger: false,
    roundMoney: legacyAppRound2,
  };

  const uni = runUnifiedBookingPricing(unifiedInput);
  if (uni.model.status !== "ok" || uni.model.finalChargePrice == null) {
    return null;
  }

  const m = uni.model;
  const hrCommission = legacyAppRound2(
    (Number(m.hrCommission?.amount) || 0) +
      (Number(m.agentCommission?.amount) || 0)
  );

  return {
    hrCommission,
    finalDisplayPrice: m.finalChargePrice,
    unifiedModel: m,
  };
}

/**
 * @param {object} p
 * @param {function(object, object=): object|null} p.calculateLDSDealFare
 * @param {object} p.currencyRate
 * @param {string[]|null} [p.categories]
 */
export function buildDynamicPackageSearchResultSsotPricing(p = {}) {
  const {
    hotelsItem = null,
    searchPhrase = null,
    currencyRate = {},
    calculateLDSDealFare,
    isB2CMode = false,
    getHrCommissionPercent,
    getHrCommissionPercentHotels,
    getAgentCommissionPercent,
    getCreditCardFeePercent,
    hrMarginPercentOverride,
    hrMarginSourceFeatureKey,
    channelTypeOverride,
  } = p;

  const emptyFlight = {
    rawTotal: null,
    supplierCommission: null,
    netTotal: null,
    currency: null,
    dealId: null,
    preHrIls: null,
  };

  const emptyHotel = {
    netTotal: null,
    currency: null,
    source: null,
    preHrIls: null,
  };

  const emptyPackage = {
    combinedNet: null,
    hrCommission: null,
    finalDisplayPrice: null,
    currency: "ILS",
    unifiedHrMarginSourceFeatureKey: null,
  };

  if (typeof calculateLDSDealFare !== "function") {
    return {
      flight: emptyFlight,
      hotel: emptyHotel,
      package: emptyPackage,
      ok: false,
      reason: "missing_calculateLDSDealFare",
    };
  }

  const bestFlight = resolveCheapestFlightDealForDynamicPackageSsot(p);

  const hotelEval = hotelPreHrIlsFromListRow(
    hotelsItem,
    searchPhrase,
    currencyRate
  );

  const flightBlock = bestFlight
    ? {
        rawTotal: bestFlight.rawTotal,
        supplierCommission: bestFlight.supplierCommission,
        netTotal: bestFlight.netTotal,
        currency: bestFlight.currency,
        dealId: bestFlight.dealId,
        preHrIls: bestFlight.preHrIls,
      }
    : emptyFlight;

  const hotelBlock = {
    netTotal: hotelEval.preHrIls,
    currency: hotelEval.preHrIls != null ? "ILS" : hotelEval.currency,
    source: hotelEval.source,
    preHrIls: hotelEval.preHrIls,
  };

  const combinedNet =
    bestFlight?.preHrIls != null && hotelEval.preHrIls != null
      ? legacyAppRound2(bestFlight.preHrIls + hotelEval.preHrIls)
      : null;

  const hrLayer =
    combinedNet != null
      ? applyHrOnCombinedNetIls(combinedNet, {
          isB2CMode,
          getHrCommissionPercent,
          getHrCommissionPercentHotels,
          getAgentCommissionPercent,
          getCreditCardFeePercent,
          hrMarginPercentOverride,
          channelTypeOverride,
        })
      : null;

  const packageBlock = {
    combinedNet,
    hrCommission: hrLayer?.hrCommission ?? null,
    finalDisplayPrice: hrLayer?.finalDisplayPrice ?? null,
    currency: "ILS",
    unifiedHrMarginSourceFeatureKey:
      hrMarginSourceFeatureKey != null &&
      String(hrMarginSourceFeatureKey).trim() !== ""
        ? String(hrMarginSourceFeatureKey)
        : null,
  };

  const result = {
    flight: flightBlock,
    hotel: hotelBlock,
    package: packageBlock,
    ok: Boolean(packageBlock.finalDisplayPrice != null),
    reason: packageBlock.finalDisplayPrice == null ? "incomplete_inputs" : null,
  };

  if (import.meta.env.DEV && isHrDpPerfDebugEnabled()) {
    try {
      // eslint-disable-next-line no-console -- opt-in via HR_DP_PERF_DEBUG
      console.log("[DYNAMIC_PACKAGE_SSOT_TRACE]", {
        cheapestFlight: bestFlight
          ? {
              dealId: bestFlight.dealId,
              rawTotal: bestFlight.rawTotal,
              supplierCommission: bestFlight.supplierCommission,
              netTotal: bestFlight.netTotal,
              preHrIls: bestFlight.preHrIls,
              currency: bestFlight.currency,
            }
          : null,
        cheapestHotel: {
          netTotalIlsPreHr: hotelEval.preHrIls,
          source: hotelEval.source,
        },
        combinedNetPackage: combinedNet,
        hrCommission: packageBlock.hrCommission,
        finalDisplayPrice: packageBlock.finalDisplayPrice,
        currency: packageBlock.currency,
        unifiedHrMarginSourceFeatureKey: packageBlock.unifiedHrMarginSourceFeatureKey,
      });
    } catch {
      /* ignore */
    }
  }

  return result;
}

/**
 * שורות פירוט לכרטיס (מחרוזות מוכנות תצוגה).
 * @param {object} ssot
 * @param {object|null|undefined} profilePriceContext — כשמועבר (פרופיל חבילה דינמית), פירוט SSOT מעוצב ב־₪ (ILS) בלבד; אחרת נשארים ב־₪ (כרטיסי חיפוש / checkout).
 */
export function buildDynamicPackageSsotBreakdownRowsHe(ssot, profilePriceContext = null) {
  if (!ssot?.package?.finalDisplayPrice) return [];

  const f = ssot.flight;
  const h = ssot.hotel;
  const p = ssot.package;

  const fmtIlsSlice = (ilsVal) => {
    const n = Number(ilsVal);
    if (!Number.isFinite(n)) return "—";
    if (profilePriceContext && typeof profilePriceContext === "object") {
      const s = formatDynamicPackageProfileSsotPackageFinalForDisplay(n, profilePriceContext);
      return s != null ? s : formatIlsDisplay(n);
    }
    return formatIlsDisplay(n);
  };

  const rows = [];

  if (f?.netTotal != null && f?.currency) {
    const rawStr = Number.isFinite(Number(f.rawTotal))
      ? `${f.rawTotal} ${f.currency}`
      : "—";
    const netStr = `${f.netTotal} ${f.currency}`;
    rows.push({
      key: "flight",
      label: "מחיר טיסה (ספק)",
      value: `ברוטו ${rawStr} · נטו ${netStr} · לפני HR (מקביל ILS) ${fmtIlsSlice(f.preHrIls)}`,
    });
  } else {
    rows.push({
      key: "flight",
      label: "מחיר טיסה",
      value: "לא זמין",
    });
  }

  rows.push({
    key: "hotel",
    label: "מחיר מלון (נטו לפני HR)",
    value:
      h?.preHrIls != null
        ? fmtIlsSlice(h.preHrIls)
        : "לא זמין",
  });

  rows.push({
    key: "combined",
    label: "סה״כ נטו (לפני HR)",
    value: p.combinedNet != null ? fmtIlsSlice(p.combinedNet) : "—",
  });

  rows.push({
    key: "hr",
    label: "עמלת HR (מאוחד)",
    value: p.hrCommission != null ? fmtIlsSlice(p.hrCommission) : "—",
  });

  rows.push({
    key: "total",
    label: "מחיר חבילה",
    value: fmtIlsSlice(p.finalDisplayPrice),
  });

  return rows;
}
